/** @jsxImportSource @emotion/react */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import logo from '../../assets/logo.svg';
import { keyframes } from '@emotion/react';
import { PlatformStatus } from '../../../types/enums.js';
import type { OnboardingFormData } from '../../../shared/types/onboarding';
import * as Sentry from '@sentry/react';

// Define the animation
const gradientMove = keyframes`
  0% { background-position: 0% 50%; }
  100% { background-position: 200% 50%; }
`;

// US States
const US_STATES = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' }
];

// Common styles
const inputClass = "mt-1 block w-full rounded-md bg-[#5E150F] border border-[#F7E8CC]/10 text-[#F7E8CC] placeholder-[#F7E8CC]/30 focus:border-[#F7E8CC]/20 focus:ring-[#F7E8CC]/20 text-base px-3 py-2.5";
const selectClass = "mt-1 block w-full rounded-md bg-[#5E150F] border border-[#F7E8CC]/10 text-[#F7E8CC] focus:border-[#F7E8CC]/20 focus:ring-[#F7E8CC]/20 text-base px-3 py-2.5";
const labelClass = "block text-base font-medium text-[#F7E8CC] mb-1";
const errorClass = "mt-1 text-sm text-red-300";
const sectionClass = "bg-[#6E1912] rounded-lg shadow-lg p-8";
const helperTextClass = "mt-1 text-sm text-[#F7E8CC]/50";
const buttonClass = "mt-2 inline-flex items-center px-3 py-2 border border-[#F7E8CC]/10 rounded-md text-[#F7E8CC] bg-[#5E150F] hover:bg-[#4A110D] focus:outline-none focus:ring-2 focus:ring-[#F7E8CC]/10";
const radioClass = "appearance-none h-4 w-4 rounded-full border-2 border-[#F7E8CC]/40 bg-[#5E150F] checked:bg-[#F7E8CC] checked:ring-[#6E1912] checked:ring-2 checked:ring-inset hover:bg-[#F7E8CC] focus:ring-[#6E1912] focus:ring-offset-[#7B1C14] focus:ring-2 focus:outline-none cursor-pointer";

interface FormErrors {
  [key: string]: string;
}

interface OnboardingFormProps {
  initialData: {
    email: string;
    brandId: string;
    offerId: string;
    token: string;
    brand: {
      id: string;
      name: string;
      logo?: string;
    };
  };
}

// Add these utility functions at the top level
const cleanHandle = (handle: string): string => {
  // Remove whitespace and @ symbol from start/end
  return handle.trim().replace(/^@+|@+$/g, '');
};

const validateHandle = (handle: string, platform: 'tiktok' | 'instagram' | 'youtube'): { isValid: boolean; error?: string } => {
  const cleaned = cleanHandle(handle);
  
  if (!cleaned) {
    return { isValid: false, error: `${platform.charAt(0).toUpperCase() + platform.slice(1)} handle is required` };
  }

  // Platform-specific validations
  switch (platform) {
    case 'tiktok':
      if (cleaned.length < 2 || cleaned.length > 24) {
        return { isValid: false, error: 'TikTok handle must be between 2 and 24 characters' };
      }
      if (!/^[a-zA-Z0-9_]+$/.test(cleaned)) {
        return { isValid: false, error: 'TikTok handle can only contain letters, numbers, and underscores' };
      }
      break;

    case 'instagram':
      if (cleaned.length > 30) {
        return { isValid: false, error: 'Instagram handle cannot exceed 30 characters' };
      }
      if (!/^[a-zA-Z0-9._]+$/.test(cleaned)) {
        return { isValid: false, error: 'Instagram handle can only contain letters, numbers, dots, and underscores' };
      }
      if (cleaned.includes('..')) {
        return { isValid: false, error: 'Instagram handle cannot contain consecutive dots' };
      }
      break;

    case 'youtube':
      if (cleaned.length < 3) {
        return { isValid: false, error: 'YouTube handle must be at least 3 characters' };
      }
      if (!/^[a-zA-Z0-9-]+$/.test(cleaned)) {
        return { isValid: false, error: 'YouTube handle can only contain letters, numbers, and hyphens' };
      }
      break;
  }

  return { isValid: true };
};

export function OnboardingForm({ initialData }: OnboardingFormProps) {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [formData, setFormData] = useState<OnboardingFormData>({
    email: initialData.email,
    firstName: '',
    lastName: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zipCode: '',
    discordStatus: undefined,
    genevaStatus: undefined,
    snowballStatus: undefined,
    tiktokHandle: '',
    instagramHandle: '',
    youtubeHandle: '',
    hasReadBrief: false,
    // Bank Details
    bankFirstName: '',
    bankLastName: '',
    bankAccountNumber: '',
    bankRoutingNumber: '',
    bankAccountType: '',
    // Payment Methods
    venmoHandle: '',
    zelleEmail: '',
    zellePhone: ''
  });
  const [stateQuery, setStateQuery] = useState('');
  const [showStateResults, setShowStateResults] = useState(false);

  const filteredStates = stateQuery
    ? US_STATES.filter(state => 
        state.label.toLowerCase().includes(stateQuery.toLowerCase()) ||
        state.value.toLowerCase().includes(stateQuery.toLowerCase())
      )
    : [];

  const validatePage1 = (): boolean => {
    const newErrors: FormErrors = {};

    // Required fields
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.addressLine1.trim()) newErrors.addressLine1 = 'Address is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.state.trim()) newErrors.state = 'State is required';
    if (!formData.zipCode.trim()) newErrors.zipCode = 'ZIP code is required';

    // Platform status validations
    if (!formData.discordStatus) newErrors.discordStatus = 'Please indicate your Discord status';
    if (!formData.genevaStatus) newErrors.genevaStatus = 'Please indicate your Geneva status';
    if (!formData.snowballStatus) newErrors.snowballStatus = 'Please indicate your Social Snowball status';

    // Social media handle validations
    const tiktokValidation = validateHandle(formData.tiktokHandle || '', 'tiktok');
    if (!tiktokValidation.isValid) {
      newErrors.tiktokHandle = tiktokValidation.error || 'Invalid TikTok handle';
    }

    const instagramValidation = validateHandle(formData.instagramHandle || '', 'instagram');
    if (!instagramValidation.isValid) {
      newErrors.instagramHandle = instagramValidation.error || 'Invalid Instagram handle';
    }

    const youtubeValidation = validateHandle(formData.youtubeHandle || '', 'youtube');
    if (!youtubeValidation.isValid) {
      newErrors.youtubeHandle = youtubeValidation.error || 'Invalid YouTube handle';
    }

    // Content brief validation
    if (!formData.hasReadBrief) {
      newErrors.hasReadBrief = 'Please confirm you have read the content brief';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateForm = (): boolean => {
    if (!validatePage1()) return false;

    const newErrors: FormErrors = {};

    // Bank account validations
    if (!formData.bankFirstName.trim()) newErrors.bankFirstName = 'First name is required';
    if (!formData.bankLastName.trim()) newErrors.bankLastName = 'Last name is required';
    if (!formData.bankAccountType) newErrors.bankAccountType = 'Account type is required';
    if (!formData.bankRoutingNumber.trim()) {
      newErrors.bankRoutingNumber = 'Routing number is required';
    } else if (!/^\d{9}$/.test(formData.bankRoutingNumber)) {
      newErrors.bankRoutingNumber = 'Routing number must be 9 digits';
    }
    if (!formData.bankAccountNumber.trim()) newErrors.bankAccountNumber = 'Account number is required';

    // Optional Zelle email validation
    if (formData.zelleEmail && !formData.zelleEmail.includes('@')) {
      newErrors.zelleEmail = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the errors before submitting');
      return;
    }

    setIsSubmitting(true);

    try {
      // Clean handles before submitting
      const cleanedFormData = {
        ...formData,
        tiktokHandle: cleanHandle(formData.tiktokHandle || ''),
        instagramHandle: cleanHandle(formData.instagramHandle || ''),
        youtubeHandle: cleanHandle(formData.youtubeHandle || ''),
        bankFirstName: formData.bankFirstName.trim(),
        bankLastName: formData.bankLastName.trim(),
        bankAccountNumber: formData.bankAccountNumber.trim(),
        bankRoutingNumber: formData.bankRoutingNumber.trim(),
        bankAccountType: formData.bankAccountType.trim(),
        venmoHandle: formData.venmoHandle?.trim(),
        zelleEmail: formData.zelleEmail?.trim(),
        zellePhone: formData.zellePhone?.trim()
      };

      const response = await fetch(`/api/public/onboarding/${initialData.token}/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(cleanedFormData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.error || 'Failed to submit form');
      }

      toast.success('Onboarding information submitted successfully!');
      // Redirect to confirmation page
      navigate('/onboarding/confirmation');
    } catch (error) {
      Sentry.captureException(error);
      console.error('Onboarding submission error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to submit onboarding information. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateFormField = (field: keyof OnboardingFormData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    try {
      const value = e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value;
      setFormData(prev => ({ ...prev, [field]: value }));
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  // Helper to show field error
  const showError = (field: keyof FormErrors) => {
    return errors[field] ? (
      <p className={errorClass}>{errors[field]}</p>
    ) : null;
  };

  // Update the handle input change handler to show live validation
  const handleSocialInputChange = (field: 'tiktokHandle' | 'instagramHandle' | 'youtubeHandle') => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    try {
      const value = e.target.value;
      setFormData(prev => ({ ...prev, [field]: value }));
      
      // Live validation
      const platform = field.replace('Handle', '') as 'tiktok' | 'instagram' | 'youtube';
      const validation = validateHandle(value, platform);
      if (!validation.isValid && validation.error) {
        setErrors(prev => ({ ...prev, [field]: validation.error as string }));
      } else {
        setErrors(prev => {
          const { [field]: _, ...rest } = prev;
          return rest;
        });
      }
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  // Add validation on change for bank routing number
  const handleBankRoutingNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const value = e.target.value;
      // Only allow digits
      if (value && !/^\d*$/.test(value)) return;
      setFormData(prev => ({ ...prev, bankRoutingNumber: value }));
      
      // Live validation
      if (!value) {
        setErrors(prev => ({ ...prev, bankRoutingNumber: 'Routing number is required' }));
      } else if (value.length !== 9) {
        setErrors(prev => ({ ...prev, bankRoutingNumber: 'Routing number must be 9 digits' }));
      } else {
        setErrors(prev => {
          const { bankRoutingNumber, ...rest } = prev;
          return rest;
        });
      }
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  return (
    <div className="min-h-screen bg-[#7B1C14] py-12 px-4 sm:px-6 lg:px-8 font-sans">
      <div className="max-w-3xl mx-auto space-y-8">
        {/* Header Section */}
        <div className="text-center mb-8">
          <img 
            src={logo} 
            alt="Spicy Cubes" 
            className="h-16 sm:h-20 md:h-24 mx-auto"
          />
        </div>
        
        <div className={sectionClass}>
          <div className="relative pb-6 mb-6 border-b border-[#F7E8CC]/10">
            <h1 className="text-3xl font-bold text-[#F7E8CC] text-center">Spicy Cubes Onboarding</h1>
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-48 h-[2px] bg-gradient-to-r from-transparent via-[#F7E8CC]/10 to-transparent" />
          </div>

          {currentPage === 1 && (
            <p className="text-[#F7E8CC]/80 mb-4">
              Please ensure you enter your address correctly and well-formatted.
              After submitting this form you will be fully onboarded and ready to start posting.
              Please follow onboarding instructions carefully.
            </p>
          )}

          {currentPage === 2 && (
            <p className="text-[#F7E8CC]/80 mb-4">
              Please provide your payment details. This information is required to process your payments.
              All information is securely stored and encrypted.
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {currentPage === 1 ? (
            <>
              {/* Basic Info Section */}
              <div className={sectionClass}>
                <div className="space-y-6">
                  <div>
                    <label htmlFor="email" className={labelClass}>
                      Email <span className="text-red-300">*</span>
                    </label>
                    <input
                      id="email"
                      type="email"
                      required
                      value={formData.email}
                      disabled
                      className={`${inputClass} bg-opacity-50 cursor-not-allowed`}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="firstName" className={labelClass}>
                        First Name <span className="text-red-300">*</span>
                      </label>
                      <input
                        id="firstName"
                        type="text"
                        required
                        value={formData.firstName}
                        onChange={updateFormField('firstName')}
                        placeholder="First Name"
                        className={`${inputClass} ${errors.firstName ? 'border-red-500' : ''}`}
                      />
                      {showError('firstName')}
                    </div>

                    <div>
                      <label htmlFor="lastName" className={labelClass}>
                        Last Name <span className="text-red-300">*</span>
                      </label>
                      <input
                        id="lastName"
                        type="text"
                        required
                        value={formData.lastName}
                        onChange={updateFormField('lastName')}
                        placeholder="Last Name"
                        className={`${inputClass} ${errors.lastName ? 'border-red-500' : ''}`}
                      />
                      {showError('lastName')}
                    </div>
                  </div>
                </div>
              </div>

              {/* Address Section */}
              <div className={sectionClass}>
                <div className="space-y-6">
                  <div>
                    <label htmlFor="addressLine1" className={labelClass}>
                      Address Line 1 <span className="text-red-300">*</span>
                    </label>
                    <input
                      id="addressLine1"
                      type="text"
                      required
                      value={formData.addressLine1}
                      onChange={updateFormField('addressLine1')}
                      placeholder="Street address"
                      className={`${inputClass} ${errors.addressLine1 ? 'border-red-500' : ''}`}
                    />
                    {showError('addressLine1')}
                  </div>

                  <div>
                    <label htmlFor="addressLine2" className={labelClass}>
                      Address Line 2
                    </label>
                    <input
                      id="addressLine2"
                      type="text"
                      value={formData.addressLine2}
                      onChange={updateFormField('addressLine2')}
                      placeholder="Apartment, suite, etc."
                      className={`${inputClass} ${errors.addressLine2 ? 'border-red-500' : ''}`}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="city" className={labelClass}>
                        City <span className="text-red-300">*</span>
                      </label>
                      <input
                        id="city"
                        type="text"
                        required
                        value={formData.city}
                        onChange={updateFormField('city')}
                        placeholder="City"
                        className={`${inputClass} ${errors.city ? 'border-red-500' : ''}`}
                      />
                      {showError('city')}
                    </div>

                    <div>
                      <label htmlFor="state" className={labelClass}>
                        State <span className="text-red-300">*</span>
                      </label>
                      <div className="relative">
                        <input
                          id="state"
                          type="text"
                          required
                          value={stateQuery}
                          onChange={(e) => {
                            setStateQuery(e.target.value);
                            setShowStateResults(true);
                          }}
                          onFocus={() => setShowStateResults(true)}
                          placeholder="Type to search..."
                          className={`${inputClass} ${errors.state ? 'border-red-500' : ''}`}
                        />
                        {showStateResults && filteredStates.length > 0 && (
                          <div 
                            className="absolute z-10 w-full mt-1 bg-[#5E150F] border border-[#F7E8CC]/10 rounded-md shadow-lg max-h-60 overflow-auto"
                          >
                            {filteredStates.map(state => (
                              <button
                                key={state.value}
                                type="button"
                                className="w-full text-left px-4 py-2 text-[#F7E8CC] hover:bg-[#4A110D] focus:bg-[#4A110D] focus:outline-none"
                                onClick={() => {
                                  setFormData(prev => ({ ...prev, state: state.value }));
                                  setStateQuery(state.label);
                                  setShowStateResults(false);
                                }}
                              >
                                {state.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <input 
                        type="hidden" 
                        name="state" 
                        value={formData.state}
                        required 
                      />
                      {showError('state')}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="zipCode" className={labelClass}>
                      ZIP Code <span className="text-red-300">*</span>
                    </label>
                    <input
                      id="zipCode"
                      type="text"
                      required
                      value={formData.zipCode}
                      onChange={updateFormField('zipCode')}
                      placeholder="ZIP Code"
                      className={`${inputClass} ${errors.zipCode ? 'border-red-500' : ''}`}
                    />
                    {showError('zipCode')}
                  </div>
                </div>
              </div>

              {/* Platform Integration Section */}
              <div className={sectionClass}>
                <h3 className="text-xl font-semibold text-[#F7E8CC] mb-6">Platform Integration</h3>
                
                {/* Discord */}
                <div className="mb-6">
                  <label htmlFor="discordStatus" className={labelClass}>
                    Discord Status <span className="text-red-300">*</span>
                  </label>
                  <p className={helperTextClass}>
                    Click to join our discord <a href="https://discord.gg/TUjBDZvmyq" className="text-[#F7E8CC] underline">LINK</a> (download Discord app)
                  </p>
                  <div className="mt-2 space-y-2">
                    <div className="flex items-center">
                      <input
                        id="discordJoined"
                        type="radio"
                        name="discordStatus"
                        value={PlatformStatus.JOINED}
                        checked={formData.discordStatus === PlatformStatus.JOINED}
                        onChange={updateFormField('discordStatus')}
                        className={`${radioClass} ${errors.discordStatus ? 'border-red-500' : ''}`}
                      />
                      <label htmlFor="discordJoined" className="ml-2 text-sm text-[#F7E8CC]">
                        I have successfully joined the discord
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        id="discordIssues"
                        type="radio"
                        name="discordStatus"
                        value={PlatformStatus.ISSUES}
                        checked={formData.discordStatus === PlatformStatus.ISSUES}
                        onChange={updateFormField('discordStatus')}
                        className={`${radioClass} ${errors.discordStatus ? 'border-red-500' : ''}`}
                      />
                      <label htmlFor="discordIssues" className="ml-2 text-sm text-[#F7E8CC]">
                        I am having issues joining the discord (send support email to: ashli@spicycubes.co)
                      </label>
                    </div>
                  </div>
                  {showError('discordStatus')}
                </div>

                {/* Geneva */}
                <div className="mb-6">
                  <label htmlFor="genevaStatus" className={labelClass}>
                    Geneva Status <span className="text-red-300">*</span>
                  </label>
                  <p className={helperTextClass}>
                    Click to join our geneva <a href="https://links.geneva.com/invite/1c89f2df-e2b8-4d57-b869-b2434f8f970c" className="text-[#F7E8CC] underline">LINK</a> (download Geneva app)
                  </p>
                  <div className="mt-2 space-y-2">
                    <div className="flex items-center">
                      <input
                        id="genevaJoined"
                        type="radio"
                        name="genevaStatus"
                        value={PlatformStatus.JOINED}
                        checked={formData.genevaStatus === PlatformStatus.JOINED}
                        onChange={updateFormField('genevaStatus')}
                        className={`${radioClass} ${errors.genevaStatus ? 'border-red-500' : ''}`}
                      />
                      <label htmlFor="genevaJoined" className="ml-2 text-sm text-[#F7E8CC]">
                        I have successfully joined geneva
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        id="genevaIssues"
                        type="radio"
                        name="genevaStatus"
                        value={PlatformStatus.ISSUES}
                        checked={formData.genevaStatus === PlatformStatus.ISSUES}
                        onChange={updateFormField('genevaStatus')}
                        className={`${radioClass} ${errors.genevaStatus ? 'border-red-500' : ''}`}
                      />
                      <label htmlFor="genevaIssues" className="ml-2 text-sm text-[#F7E8CC]">
                        I am having issues joining geneva (send support email to: ashli@spicycubes.co)
                      </label>
                    </div>
                  </div>
                  {showError('genevaStatus')}
                </div>

                {/* Social Snowball */}
                <div className="mb-6">
                  <label htmlFor="snowballStatus" className={labelClass}>
                    Social Snowball Status <span className="text-red-300">*</span>
                  </label>
                  <p className={helperTextClass}>
                    Click to get your affiliate code <a href="https://spicycubes.co/pages/promote-our-brand" className="text-[#F7E8CC] underline">LINK</a>
                  </p>
                  <div className="mt-2 space-y-2">
                    <div className="flex items-center">
                      <input
                        id="snowballJoined"
                        type="radio"
                        name="snowballStatus"
                        value={PlatformStatus.JOINED}
                        checked={formData.snowballStatus === PlatformStatus.JOINED}
                        onChange={updateFormField('snowballStatus')}
                        className={`${radioClass} ${errors.snowballStatus ? 'border-red-500' : ''}`}
                      />
                      <label htmlFor="snowballJoined" className="ml-2 text-sm text-[#F7E8CC]">
                        I have successfully gotten my social snowball affiliate code
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        id="snowballIssues"
                        type="radio"
                        name="snowballStatus"
                        value={PlatformStatus.ISSUES}
                        checked={formData.snowballStatus === PlatformStatus.ISSUES}
                        onChange={updateFormField('snowballStatus')}
                        className={`${radioClass} ${errors.snowballStatus ? 'border-red-500' : ''}`}
                      />
                      <label htmlFor="snowballIssues" className="ml-2 text-sm text-[#F7E8CC]">
                        I am having issues getting my code (send support email to: ashli@spicycubes.co)
                      </label>
                    </div>
                  </div>
                  {showError('snowballStatus')}
                </div>
              </div>

              {/* Social Media Handles Section */}
              <div className={sectionClass}>
                <h3 className="text-xl font-semibold text-[#F7E8CC] mb-6">Social Media Handles</h3>
                
                {/* TikTok */}
                <div className="mb-6">
                  <label htmlFor="tiktokHandle" className={labelClass}>
                    TikTok Handle <span className="text-red-300">*</span>
                  </label>
                  <p className={helperTextClass}>
                    Follow this guide to create your TikTok account: <a href="/tiktok-account-setup" className="text-[#F7E8CC] underline">LINK</a>
                  </p>
                  <div className="mt-2">
                    <input
                      id="tiktokHandle"
                      type="text"
                      required
                      value={formData.tiktokHandle}
                      onChange={handleSocialInputChange('tiktokHandle')}
                      onBlur={() => {
                        const cleaned = cleanHandle(formData.tiktokHandle || '');
                        setFormData(prev => ({ ...prev, tiktokHandle: cleaned ? `@${cleaned}` : '' }));
                      }}
                      placeholder="@myhandle"
                      className={`${inputClass} ${errors.tiktokHandle ? 'border-red-500' : ''}`}
                    />
                    {showError('tiktokHandle')}
                  </div>
                </div>

                {/* Instagram */}
                <div className="mb-6">
                  <label htmlFor="instagramHandle" className={labelClass}>
                    Instagram Handle <span className="text-red-300">*</span>
                  </label>
                  <p className={helperTextClass}>
                    Follow this guide to create your Instagram account: <a href="/instagram-account-setup" className="text-[#F7E8CC] underline">LINK</a>
                  </p>
                  <div className="mt-2">
                    <input
                      id="instagramHandle"
                      type="text"
                      required
                      value={formData.instagramHandle}
                      onChange={handleSocialInputChange('instagramHandle')}
                      onBlur={() => {
                        const cleaned = cleanHandle(formData.instagramHandle || '');
                        setFormData(prev => ({ ...prev, instagramHandle: cleaned ? `@${cleaned}` : '' }));
                      }}
                      placeholder="@myhandle"
                      className={`${inputClass} ${errors.instagramHandle ? 'border-red-500' : ''}`}
                    />
                    {showError('instagramHandle')}
                  </div>
                </div>

                {/* YouTube */}
                <div className="mb-6">
                  <label htmlFor="youtubeHandle" className={labelClass}>
                    YouTube Shorts Handle <span className="text-red-300">*</span>
                  </label>
                  <p className={helperTextClass}>
                    Follow this guide to create your YouTube Shorts account: <a href="/youtube-shorts-account-setup" className="text-[#F7E8CC] underline">LINK</a>
                  </p>
                  <div className="mt-2">
                    <input
                      id="youtubeHandle"
                      type="text"
                      required
                      value={formData.youtubeHandle}
                      onChange={handleSocialInputChange('youtubeHandle')}
                      onBlur={() => {
                        const cleaned = cleanHandle(formData.youtubeHandle || '');
                        setFormData(prev => ({ ...prev, youtubeHandle: cleaned ? `@${cleaned}` : '' }));
                      }}
                      placeholder="@myhandle"
                      className={`${inputClass} ${errors.youtubeHandle ? 'border-red-500' : ''}`}
                    />
                    {showError('youtubeHandle')}
                  </div>
                </div>
              </div>

              {/* Content Brief Section */}
              <div className={sectionClass}>
                <div className="space-y-4">
                  <label htmlFor="hasReadBrief" className={labelClass}>
                    Content Brief <span className="text-red-300">*</span>
                  </label>
                  <p className={helperTextClass}>
                    Read our content brief (password: <span className="font-bold text-[#FFD700]">spicypeach</span>): <a href="/content-brief" className="text-[#F7E8CC] underline">LINK</a>
                  </p>
                  <div className="flex items-center">
                    <input
                      id="hasReadBrief"
                      type="checkbox"
                      checked={formData.hasReadBrief}
                      onChange={updateFormField('hasReadBrief')}
                      className="h-4 w-4 rounded border-[#F7E8CC]/40 bg-[#5E150F] text-[#F7E8CC] focus:ring-[#6E1912] ${errors.hasReadBrief ? 'border-red-500' : ''} focus:ring-[#6E1912]"
                    />
                    <label htmlFor="hasReadBrief" className="ml-2 text-sm text-[#F7E8CC]">
                      DONE
                    </label>
                  </div>
                  {showError('hasReadBrief')}
                </div>
              </div>

              {/* Navigation Buttons */}
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => {
                    if (validatePage1()) {
                      setCurrentPage(2);
                      window.scrollTo(0, 0);
                    } else {
                      toast.error('Please fix the errors before proceeding');
                    }
                  }}
                  className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-base font-medium text-[#F7E8CC] bg-[#5E150F] hover:bg-[#4A110D] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F7E8CC]/10"
                >
                  Next
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Page 2 - Payment Details */}
              <div className={sectionClass}>
                <h3 className="text-xl font-semibold text-[#F7E8CC] mb-6">Bank Account Information</h3>
                
                <div className="space-y-6">
                  {/* Bank Account Details */}
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="bankFirstName" className={labelClass}>
                        First Name on Account <span className="text-red-300">*</span>
                      </label>
                      <input
                        id="bankFirstName"
                        type="text"
                        required
                        value={formData.bankFirstName}
                        onChange={updateFormField('bankFirstName')}
                        className={`${inputClass} ${errors.bankFirstName ? 'border-red-500' : ''}`}
                      />
                      {showError('bankFirstName')}
                    </div>

                    <div>
                      <label htmlFor="bankLastName" className={labelClass}>
                        Last Name on Account <span className="text-red-300">*</span>
                      </label>
                      <input
                        id="bankLastName"
                        type="text"
                        required
                        value={formData.bankLastName}
                        onChange={updateFormField('bankLastName')}
                        className={`${inputClass} ${errors.bankLastName ? 'border-red-500' : ''}`}
                      />
                      {showError('bankLastName')}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="bankAccountType" className={labelClass}>
                      Account Type <span className="text-red-300">*</span>
                    </label>
                    <select
                      id="bankAccountType"
                      required
                      value={formData.bankAccountType}
                      onChange={updateFormField('bankAccountType')}
                      className={`${selectClass} ${errors.bankAccountType ? 'border-red-500' : ''}`}
                    >
                      <option value="">Select account type</option>
                      <option value="checking">Checking</option>
                      <option value="savings">Savings</option>
                    </select>
                    {showError('bankAccountType')}
                  </div>

                  <div>
                    <label htmlFor="bankRoutingNumber" className={labelClass}>
                      Routing Number <span className="text-red-300">*</span>
                    </label>
                    <input
                      id="bankRoutingNumber"
                      type="text"
                      required
                      maxLength={9}
                      value={formData.bankRoutingNumber}
                      onChange={handleBankRoutingNumberChange}
                      className={`${inputClass} ${errors.bankRoutingNumber ? 'border-red-500' : ''}`}
                    />
                    {showError('bankRoutingNumber')}
                  </div>

                  <div>
                    <label htmlFor="bankAccountNumber" className={labelClass}>
                      Account Number <span className="text-red-300">*</span>
                    </label>
                    <input
                      id="bankAccountNumber"
                      type="text"
                      required
                      value={formData.bankAccountNumber}
                      onChange={updateFormField('bankAccountNumber')}
                      className={`${inputClass} ${errors.bankAccountNumber ? 'border-red-500' : ''}`}
                    />
                    {showError('bankAccountNumber')}
                  </div>
                </div>
              </div>

              {/* Alternative Payment Methods */}
              <div className={sectionClass}>
                <h3 className="text-xl font-semibold text-[#F7E8CC] mb-6">Alternative Payment Methods</h3>
                <p className={helperTextClass}>Optional: Provide alternative payment methods for faster payments</p>
                
                <div className="space-y-6 mt-4">
                  <div>
                    <label htmlFor="venmoHandle" className={labelClass}>
                      Venmo Handle
                    </label>
                    <input
                      id="venmoHandle"
                      type="text"
                      value={formData.venmoHandle}
                      onChange={updateFormField('venmoHandle')}
                      placeholder="@username"
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <label htmlFor="zelleEmail" className={labelClass}>
                      Zelle Email
                    </label>
                    <input
                      id="zelleEmail"
                      type="email"
                      value={formData.zelleEmail}
                      onChange={updateFormField('zelleEmail')}
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <label htmlFor="zellePhone" className={labelClass}>
                      Zelle Phone Number
                    </label>
                    <input
                      id="zellePhone"
                      type="tel"
                      value={formData.zellePhone}
                      onChange={updateFormField('zellePhone')}
                      placeholder="(123) 456-7890"
                      className={inputClass}
                    />
                  </div>
                </div>
              </div>

              {/* Navigation Buttons */}
              <div className="flex justify-between gap-4">
                <button
                  type="button"
                  onClick={() => {
                    setCurrentPage(1);
                    window.scrollTo(0, 0);
                  }}
                  className="w-full flex justify-center py-3 px-4 border border-[#F7E8CC]/10 rounded-md shadow-sm text-base font-medium text-[#F7E8CC] bg-transparent hover:bg-[#F7E8CC]/5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F7E8CC]/10"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-base font-medium text-[#F7E8CC] bg-[#5E150F] hover:bg-[#4A110D] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F7E8CC]/10 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </>
          )}
        </form>

        <div className="mt-8 text-center">
          <p className="text-[#F7E8CC]/80">
            For questions or support please contact{' '}
            <a 
              href="mailto:ashli@spicycubes.co" 
              className="font-bold text-[#F7E8CC] hover:text-[#F7E8CC] whitespace-nowrap underline decoration-[2px] hover:decoration-[2.5px] transition-all duration-200 [text-decoration-skip-ink:none]"
            >
              ashli@spicycubes.co
            </a>
          </p>
        </div>
      </div>
    </div>
  );
} 